import { AnimatePresence, motion } from "framer-motion";

type PillOption<T> = {
  label: string;
  value: T;
  color: string; // 'green', 'red', etc.
};

type PillSwitchProps<T> = {
  options: PillOption<T>[];
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
};

const TailwindColorHexMap: Record<string, string> = {
  red: "#ef4444",
  green: "#059669",
  blue: "#3b82f6",
  yellow: "#eab308",
  gray: "#9ca3af",
  orange: "#f97316",
  purple: "#8b5cf6",
  pink: "#ec4899",
  teal: "#14b8a6",
};

const PillSwitch = <T,>({
  options,
  disabled,
  value,
  onChange,
}: PillSwitchProps<T>) => {
  // Assume two options only
  const activeIdx = options.findIndex((opt) => opt.value === value);
  // const color = TailwindColorHexMap[options[activeIdx]?.color] || "#059669";
  const toggle = () => {
    const nextIdx = activeIdx === 0 ? 1 : 0;
    onChange(options[nextIdx].value);
  };

  return (
    <div
      className="flex items-center justify-center"
      style={{ minWidth: 160, opacity: disabled ? 0.5 : 1 }}
    >
      <div
        className="relative flex w-40 h-7 rounded-full transition-colors duration-200 cursor-pointer select-none"
        style={{ backgroundColor: "#f3f4f6" }}
        onClick={disabled ? undefined : toggle}
      >
        {/* Animated active pill */}
        <AnimatePresence initial={false}>
          {options.map((option, idx) => {
            if (value === option.value) {
              const color = TailwindColorHexMap[option.color] || "#059669";
              return (
                <motion.div
                  key={option.value as string || idx.toString()}
                  layoutId="active-pill"
                  className="absolute z-0 rounded-full"
                  initial={false}
                  animate={{
                    left: `calc(${(100 / options.length) * idx}% )`,
                    width: `calc(100% / ${options.length})`,
                    height: "100%",
                    top: 0,
                    backgroundColor: color,
                  }}
                  exit={{}}
                  transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  style={{}}
                />
              );
            }
            return null;
          })}
        </AnimatePresence>
        {/* Buttons (no onClick) */}
        {options.map((option, index) => {
          const isActive = value === option.value;
          const color = TailwindColorHexMap[option.color] || "#059669";
          return (
            <button
              key={option.value as string || index}
              className={`flex-1 flex items-center justify-center rounded-full transition-all duration-200 font-semibold text-sm  relative z-10 ${isActive ? "shadow-sm" : "hover:bg-white/60"
                } ${disabled ? "cursor-not-allowed" : ""}`}
              style={{
                backgroundColor: "transparent",
                color: isActive ? "#fff" : "#6b7280",
                boxShadow: isActive ? `0 2px 8px 0 ${color}22` : undefined,
                transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
                outline: "none",
              }}
              type="button"
              tabIndex={-1}
            // No onClick here
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PillSwitch;
